import { ReactNode, useState } from "react";
import { Box, FormControl, OutlinedInput, FormHelperText, FormLabel, Typography, Button } from "@mui/material";
import { useTranslation } from "next-i18next";
import {
    IDeclareInterestMutationError,
    IDeclareInterestResponse,
    useDeclareInterestMutation,
    useDeclareInterestQuery,
} from "src/api/client/declare-interest.api";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { WfNativeSelect } from "src/components-mui/base/wf-native-select/wf-native-select";
import { WF_DIALOG_SPACE_VARIANTS, WfDialog } from "src/components-mui/common/dialog/wf-dialog";
import { COUNTRY_INT } from "src/consts/locale";
import { useFormValidator } from "src/hooks/form-validator/use-form-validator";
import { useForm } from "src/hooks/use-form";
import { useWfToast, EnumToastStatus } from "src/hooks/use-wf-toast";
import { useUserStore } from "src/stores/user-store";
import { logger } from "src/utils/logger-util";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { DECLARE_INTEREST_FORM_TEST_ID } from "./declare-interest-dialog.test-ids";

const ID_DECLARE_INTEREST_FORM = "declare-interest-form";
const FAILED_DECLARE_INTEREST_ERROR_MESSAGE = "Failed to declare interest";

const ARIA_IDS = {
    TITLE: "declare-interest-dialog-title",
    DESCRIPTION: "declare-interest-dialog-description",
} as const;

interface IDeclareInterestDialogProps {
    wikifolioId: string;
    onRequestSettled?: () => void;
    children: (props: { handleOnClick: () => void; isLoading: boolean }) => ReactNode;
}

export const DeclareInterestDialog = (props: IDeclareInterestDialogProps) => {
    const { wikifolioId, onRequestSettled, children } = props;
    const [isOpen, setIsOpen] = useState(false);
    const toast = useWfToast();
    const { country } = useRouterLocale();
    const { t } = useTranslation("common");

    const userStore = useUserStore();
    const {
        data: responseData,
        isLoading,
        isError,
    } = useDeclareInterestQuery({
        enabled: isOpen,
        onError: () => {
            toast({ status: EnumToastStatus.Error, title: t("general-error") });
            setIsOpen(false);
        },
        onSettled: onRequestSettled,
    });

    const handleOnClick = () => {
        setIsOpen(true);
    };

    const onClose = () => {
        setIsOpen(false);
    };

    const TriggerComponent = () => children({ handleOnClick, isLoading });

    if (!isOpen || isLoading || isError || !responseData) {
        return <TriggerComponent />;
    }

    const prefilledEmail = userStore.user?.email ?? "";
    const prefilledCountry = country === COUNTRY_INT ? responseData.data.countries[0].value : country;

    return (
        <>
            <TriggerComponent />
            <DeclareInterestDialogContent {...{ wikifolioId, prefilledEmail, prefilledCountry, responseData }} isOpen={isOpen} onClose={onClose} />
        </>
    );
};

interface IFormValues {
    email: string;
    country: string;
}

interface IContentProps {
    isOpen: boolean;
    onClose: () => void;
    wikifolioId: string;
    prefilledEmail: string;
    prefilledCountry: string;
    responseData: IDeclareInterestResponse;
}

export const DeclareInterestDialogContent = (props: IContentProps) => {
    const {
        isOpen,
        onClose,
        wikifolioId,
        prefilledEmail,
        prefilledCountry,
        responseData: { data, dict },
    } = props;

    const { t } = useTranslation("common");
    const toast = useWfToast();
    const {
        register,
        handleSubmit,
        formState: { isSubmitting, errors },
        setError,
        isFieldInvalid,
    } = useForm<IFormValues>({
        mode: "onSubmit",
        defaultValues: { email: prefilledEmail, country: prefilledCountry },
    });

    const validators = useFormValidator();
    const declareInterest = useDeclareInterestMutation();

    const onSubmit = handleSubmit(async values => {
        try {
            const response = await declareInterest({
                wikifolioId,
                email: values.email,
                country: values.country,
            });
            toast({ status: EnumToastStatus.Success, title: response.message });
            onClose();
        } catch (error) {
            logger.error({ message: FAILED_DECLARE_INTEREST_ERROR_MESSAGE, error });
            Object.entries(error as IDeclareInterestMutationError).forEach(([fieldName, errorMessage]) => {
                setError(fieldName as keyof IFormValues, { message: errorMessage });
            });
        }
    });

    return (
        <WfDialog
            space={WF_DIALOG_SPACE_VARIANTS.MOBILE_SPACIOUS_TOP}
            isOpen={isOpen}
            onClose={onClose}
            ariaLabelledBy={ARIA_IDS.TITLE}
            ariaDescribedBy={ARIA_IDS.DESCRIPTION}
        >
            <Box component="header">
                <WfHeading component="h2" fontSize={{ xs: "xl", sm: "2xl" }} id={ARIA_IDS.TITLE}>
                    {dict.headline}
                </WfHeading>
                <Typography
                    id={ARIA_IDS.DESCRIPTION}
                    sx={{
                        mt: 2,
                        fontWeight: "normal",
                        fontSize: "md",
                    }}
                >
                    {dict.description}
                </Typography>
            </Box>

            <Box
                component="form"
                id={ID_DECLARE_INTEREST_FORM}
                data-test-id={DECLARE_INTEREST_FORM_TEST_ID}
                noValidate
                onSubmit={onSubmit}
                sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 3 }}
            >
                <FormControl fullWidth error={isFieldInvalid("email")}>
                    <FormLabel htmlFor="declare-interest-email">{t("email")}</FormLabel>
                    <OutlinedInput
                        id="declare-interest-email"
                        placeholder={t("email")}
                        type="email"
                        autoComplete="email"
                        autoFocus
                        {...register("email", { ...validators.email() })}
                    />
                    {errors.email?.message && <FormHelperText error>{errors.email.message}</FormHelperText>}
                </FormControl>

                <FormControl fullWidth error={isFieldInvalid("country")}>
                    <FormLabel htmlFor="declare-interest-country">{t("country")}</FormLabel>
                    <WfNativeSelect id="declare-interest-country" autoComplete="country" {...register("country", { required: true })}>
                        {data.countries.map(({ label, value }) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </WfNativeSelect>
                </FormControl>
            </Box>

            <Box component="footer" sx={{ mt: { xs: "auto", sm: 5 }, display: "flex", justifyContent: "flex-end" }}>
                <Button
                    type="submit"
                    form={ID_DECLARE_INTEREST_FORM}
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                    {dict.sendButton}
                </Button>
            </Box>
        </WfDialog>
    );
};
